import { Fragment } from 'react'

import { Controller, useFormContext } from 'react-hook-form'

import { CalendarDatePicker } from '@/components/ui/calendar/calendar-date-picker'
import { Dialog } from '@/components/ui/dialog'
import { FormSelect } from '@/components/ui/form/FormSelect.tsx'
import { Radio, RadioGroup } from '@/components/ui/radio'
import { CalendarFormInput } from '@/modules/online-mass-messages/ui/time-calendar/calendar-input'
import { WeeklyRepetitionSelector } from '@/modules/tasks/components/task-scheduler-dialog/weekly-repetition-schedule'
import { classNames } from '@/utils/helpers'

import { addOneDay } from '../../utils'
import { MultiSelect } from '../dialogs/multi-select'
import { nonRepeatOption, recurringOptions, repeatWeekly } from './consts'
import { FormItem } from './form-item'
import type { CreateEditFormProps, ShiftFormValues } from './types'
import { useShiftForm } from './use-shift-form'

export const CreateEditShiftForm = ({
  onClose,
  title,
  isAllowedToChangeRepetition,
  submitButton,
  creators,
  isEdit,
}: CreateEditFormProps) => {
  const { getValues, control } = useFormContext<ShiftFormValues>()

  const { memberOptions, creatorsOptions, handleChangeCreatorIds } =
    useShiftForm(creators, isEdit)

  const creatorField = isEdit ? (
    <FormSelect
      showError
      name="creatorId"
      label="Creator"
      className="w-full"
      options={creatorsOptions}
      placeholder="Choose a Creator"
      disabled={!creatorsOptions?.length}
      onChange={(value) => {
        handleChangeCreatorIds([value])
      }}
    />
  ) : (
    <Controller
      name="creatorIds"
      control={control}
      render={({ field: { value, onChange }, fieldState: { error } }) => (
        <FormItem
          label="Creators"
          error={error?.message}
        >
          {value && (
            <MultiSelect
              disabled={!creatorsOptions?.length}
              options={creatorsOptions}
              entity="Creators"
              placeholder="Choose Creators"
              searchPlaceholder="Search Creator"
              value={value}
              onChange={(value) => {
                handleChangeCreatorIds(value)
                onChange(value)
              }}
            />
          )}
        </FormItem>
      )}
    />
  )

  return (
    <>
      <Dialog.Header>
        <Dialog.Title>{title}</Dialog.Title>
      </Dialog.Header>
      <Dialog.Description className="mt-6 block space-y-6">
        <div className="flex w-full gap-6">
          <div className="flex-1">
            <CalendarFormInput
              name="startedAt"
              label="Start Date"
              fromDate={new Date()}
              className="bg-white dark:bg-transparent"
              format="LLL dd, y - hh:mmaaa"
              block
              showError
            />
          </div>

          <div className="flex-1">
            <CalendarFormInput
              name="finishedAt"
              label="End Date"
              fromDate={getValues('startedAt')}
              toDate={addOneDay(getValues('startedAt'))}
              className="bg-white dark:bg-transparent"
              format="LLL dd, y - hh:mmaaa"
              block
              showError
            />
          </div>
        </div>
        {creatorField}
        <Controller
          name="memberIds"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <FormItem
              label="Team Members"
              error={error?.message}
            >
              <MultiSelect
                disabled={!memberOptions.length}
                options={memberOptions}
                value={value}
                onChange={onChange}
                entity="Team Members"
                placeholder="Assign Team Member"
                searchPlaceholder="Search Team Member"
              />
            </FormItem>
          )}
        />
        <FormSelect
          name="recurring"
          label="Recurring"
          className={classNames('w-full', {
            hidden: !isAllowedToChangeRepetition,
          })}
          options={recurringOptions}
        />
        <Controller
          name="recurring"
          control={control}
          render={({ field: { value } }) =>
            value !== nonRepeatOption.value ? (
              <>
                {value === repeatWeekly.value && (
                  <div
                    className={classNames('space-y-2', {
                      hidden: !isAllowedToChangeRepetition,
                    })}
                  >
                    <WeeklyRepetitionSelector
                      name="days"
                      entityLabel="Shift"
                      showError
                    />
                  </div>
                )}
                <div className="flex space-x-6">
                  <Controller
                    name="isRepetitionInfinite"
                    control={control}
                    render={({
                      field: isRepetitionInfiniteField,
                      fieldState: { error },
                    }) => (
                      <FormItem
                        label="Last day"
                        error={error?.message}
                      >
                        <RadioGroup
                          {...isRepetitionInfiniteField}
                          className="flex space-x-6"
                        >
                          <Radio
                            label="None"
                            value="true"
                          />
                          <Radio
                            label="On"
                            value="false"
                          />
                          <Controller
                            name="endDate"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                              <CalendarDatePicker
                                date={value}
                                onChange={onChange}
                                fromDate={new Date()}
                                placeholder="End Time"
                                disabled={
                                  isRepetitionInfiniteField.value === 'true'
                                }
                              />
                            )}
                          />
                        </RadioGroup>
                      </FormItem>
                    )}
                  />
                </div>
              </>
            ) : (
              <Fragment />
            )
          }
        />
      </Dialog.Description>
      <Dialog.ClosableFooter onClose={onClose}>
        {submitButton}
      </Dialog.ClosableFooter>
    </>
  )
}
